import { EntityIcon } from '@common/Entity/EntityIcons';
import { SnowflakePwdAuthBanner } from '@common/Fabric/SnowflakePwdAuthBanner';
import { ProjectListQuery } from '@common/ProjectBrowser/query';
import SearchInput from '@common/SearchInput';
import { useToggleEntity } from '@common/sqlIde/ProjectBrowser/hooks/useToggleEntity';
import { Entity, getAppEntityName } from '@common/types/Entity';
import { getSqlIDEUrl, Private_Routes } from '@common/url';
import { renderColumnWithEllipsis, renderTimeColumnWithEllipsis } from '@common/utils/renderUtils';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { PlusIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import {
  Avatar,
  Badges,
  Breadcrumb,
  Button,
  ButtonProps,
  ColumnsType,
  Container,
  Dropdown,
  Ellipsis,
  MetadataPageContainer,
  SortOrder,
  Stack,
  StackItem,
  Table,
  TableActions,
  Text,
  Tooltip
} from '@prophecy/ui-v3';
import { CrumbTypes } from '@prophecy/ui-v3/Breadcrumb/types';
import { TABLE_CELL_CLASS } from '@prophecy/ui-v3/Table';
import { escapeRegExp } from 'lodash-es';
import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { EntityAction, SqlEntity } from '../../../../common/sqlIde/types/common';
import { useUser } from '../../../../context/user';
import { useGraphQlQuery } from '../../../../data/util';
import { fetchSQLAppList } from './api';

// import { DotsHorizontalIcon } from '@prophecy/icons';
export type AppType = {
  _id: string;
  name: string;
  description: string;
  created: number;
  createdBy: string;
  project: {
    _id: string;
    name: string;
  };
  sqlOrchestrationPipeline: {
    _id: string;
    name: string;
  };
};

const StyledBadges = styled(Badges)`
  padding: ${theme.spaces.x2} ${theme.spaces.x8} !important;
`;

const StyledTable = styled(Table)`
  thead .${TABLE_CELL_CLASS} {
    font-weight: 450;
    color: ${theme.colors.gray900};
  }

  tbody .${TABLE_CELL_CLASS} {
    color: ${theme.colors.gray700};
  }
` as typeof Table;

const StyledButton = styled(Button)`` as unknown as typeof Button;
const BreadcrumbSeparator = styled(Breadcrumb.Icon)`
  color: ${theme.colors.gray400};
  height: ${theme.sizes.x12};
  width: ${theme.sizes.x12};
  margin: 0;
`;

const StyledTableLink = styled(Button).attrs({ variant: 'link' })`
  display: inline-block;
  vertical-align: top;
  width: auto;
  min-width: 0;
  max-width: none;
  height: auto;
  color: ${theme.colors.brand600};
  font-size: inherit;
  padding: 0;
` as typeof Button;

const CreateButton = styled(Button)`
  gap: ${theme.spaces.x2};
` as unknown as typeof Button;
const DropdownContainer = styled.div`
  width: 280px;
  max-height: 360px;
  overflow: auto;
`;

// Explicit type to avoid expensive generic inference (was 684ms)
type CrumbItem = ButtonProps<CrumbTypes>;

const AppHeader = ({ crumbs }: { crumbs: CrumbItem[] }) => {
  const navigate = useNavigate();
  const crumbsLength = crumbs.length;

  const { user } = useUser();
  const uid = user?.id;
  const variables = { uid };
  const { data, isLoading } = useGraphQlQuery(
    [Entity.Project, 'list', variables],
    ProjectListQuery,
    undefined,
    variables
  );

  const sqlProjects = useMemo(() => {
    const allProjects = data?.User?.projects || [];
    return allProjects.filter((project) => project.language === BackEndCodeLanguage.sql);
  }, [data]);

  return (
    <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8} align='space-between' width='100%'>
      <Stack
        direction='horizontal'
        align='space-between'
        alignY='center'
        gap={theme.spaces.x8}
        padding={`${theme.spaces.x8} ${theme.spaces.x4}`}
        width='100%'>
        <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
          {crumbs.map(({ children, icon, ...rest }, index) => {
            const isLast = index < crumbsLength - 1;
            // Cast to Record to avoid 620ms type inference on spread
            const buttonProps = rest as Record<string, unknown>;
            return (
              <Fragment key={index}>
                <StyledButton variant='plain' data-test-id={buttonProps['data-test-id'] as string} {...buttonProps}>
                  <Stack direction='horizontal' alignY='center' gap={theme.spaces.x8}>
                    {icon}

                    <Text level='sm' weight={theme.fontWeight.medium} tone={theme.colors.gray600}>
                      {children}
                    </Text>
                  </Stack>
                </StyledButton>
                {isLast && <BreadcrumbSeparator />}
              </Fragment>
            );
          })}
        </Stack>
        <Stack alignY='center'>
          <Dropdown
            portalled={false}
            data-test-id='app-list-project-dropdown'
            overlay={
              <DropdownContainer>
                {sqlProjects.map((project) => {
                  return (
                    <Dropdown.Item
                      key={project._id}
                      onSelect={() => {
                        navigate(
                          getSqlIDEUrl({
                            uid: project._id,
                            entity: Entity.App.toLowerCase() as SqlEntity,
                            action: EntityAction.create
                          })
                        );
                      }}
                      data-test-id='project-selection-dropdown-item'>
                      <Ellipsis tooltip>{project.name}</Ellipsis>
                    </Dropdown.Item>
                  );
                })}
              </DropdownContainer>
            }>
            <CreateButton
              variant='primary'
              icon={<PlusIcon type='default' />}
              size='s'
              iconPlacement='left'
              disabled={isLoading}
              loading={isLoading}
              data-test-id='app-list-create-app-button'>
              Create App
            </CreateButton>
          </Dropdown>
        </Stack>
      </Stack>
    </Stack>
  );
};

const AvatarWithTooltip = ({ title }: { title: string }) => {
  return (
    <Tooltip title={title}>
      <Stack align='start'>
        <Avatar withBorder={true} text={title} size='xs' />
      </Stack>
    </Tooltip>
  );
};

type SearchParams = Parameters<ReturnType<typeof useToggleEntity>['openEntity']>[0];
const getColumns = (openEntity: (projectId: string, searchParams: SearchParams) => unknown): ColumnsType<AppType> => [
  {
    key: 'name',
    dataIndex: 'name',
    title: getAppEntityName(Entity.App) + ' Name',
    sortable: true,
    width: '18%',
    render: (name, record) => (
      <Stack gap={theme.spaces.x6} padding={`${theme.spaces.x6} 0`} direction='horizontal' alignY='center'>
        <EntityIcon size='xs' entity={Entity.App} tooltip={false} />
        <StyledTableLink
          onClick={() => {
            openEntity(record.project._id, {
              entity: Entity.App.toLowerCase() as SqlEntity,
              name: record.name,
              action: EntityAction.view
            });
          }}
          data-test-id={`app-list-app-name-${record._id}-link`}>
          {renderColumnWithEllipsis(name)}
        </StyledTableLink>
      </Stack>
    )
  },
  {
    key: 'project',
    dataIndex: 'project',
    title: 'Project',
    sortable: true,
    width: '15%',
    render: (project: AppType['project']) => (
      <StyledTableLink
        elementType='link'
        to={Private_Routes.Project.home.getUrl({ uid: project._id })}
        data-test-id={`app-list-project-name-${project._id}-link`}>
        {renderColumnWithEllipsis(project.name)}
      </StyledTableLink>
    )
  },
  {
    key: 'sqlOrchestrationPipeline',
    dataIndex: 'sqlOrchestrationPipeline',
    title: 'Pipeline',
    sortable: true,
    width: '15%',
    render: (pipeline: AppType['sqlOrchestrationPipeline'], record) =>
      pipeline?.name ? (
        <Stack padding={`${theme.spaces.x6} 0`} direction='horizontal' align='start' alignY='center'>
          <StyledTableLink
            onClick={() => {
              openEntity(record.project._id, {
                entity: Entity.Pipeline.toLowerCase() as SqlEntity,
                name: pipeline.name
              });
            }}
            data-test-id={`app-list-pipeline-name-${pipeline._id}-link`}>
            {renderColumnWithEllipsis(pipeline.name)}
          </StyledTableLink>
        </Stack>
      ) : null
  },
  {
    key: 'description',
    dataIndex: 'description',
    title: 'Description',
    width: '16%',
    render: renderColumnWithEllipsis
  },
  {
    title: 'Created By',
    dataIndex: 'createdBy',
    key: 'createdBy',
    sortable: true,
    width: '12%',
    render: (name: string) => (
      <Stack gap={theme.spaces.x6} direction='horizontal' alignY='center'>
        <AvatarWithTooltip title={name} />
        <StackItem grow='1'>
          <Ellipsis tooltip={true}>
            <Text level='sm'>{name}</Text>
          </Ellipsis>
        </StackItem>
      </Stack>
    )
  },
  {
    key: 'created',
    dataIndex: 'created',
    title: 'Created on',
    sortable: true,
    render: renderTimeColumnWithEllipsis
  },
  {
    key: 'open',
    align: 'right',
    render: (_, record) => (
      <TableActions gap={theme.spaces.x12} direction='horizontal' alignY='center' align='end'>
        <Button
          size='xs'
          variant='secondaryGray'
          onClick={() => {
            openEntity(record.project._id, {
              entity: Entity.App.toLowerCase() as SqlEntity,
              name: record.name,
              action: EntityAction.view
            });
          }}
          data-test-id={`app-list-open-app-${record._id}-button`}>
          Open
        </Button>
        {/* <DotsHorizontalIcon type='default' color={theme.colors.grey500} /> */}
      </TableActions>
    )
  }
];

const SQLAppListQuery = 'SQLAppListQuery';
export default function AppList() {
  const [searchStr, setSearchStr] = useState('');
  const { user } = useUser();
  const { data: { User: { sqlBusinessApps = [] } = {} } = {}, isInitialLoading: isQueryLoading } = useGraphQlQuery(
    [SQLAppListQuery],
    fetchSQLAppList,
    undefined,
    { userId: user.id }
  );

  const data = sqlBusinessApps as AppType[];
  const navigate = useNavigate();

  const filteredData = useMemo(() => {
    if (!searchStr) return data;
    const searchRegex = new RegExp(escapeRegExp(searchStr), 'i');
    return data.filter((row) => Object.values(row).some((value) => searchRegex.test(JSON.stringify(value))));
  }, [searchStr, data]);

  const header = (
    <AppHeader
      crumbs={[
        {
          icon: <EntityIcon size='s' entity={Entity.App} />,
          elementType: 'link',
          children: 'Analytical ' + getAppEntityName(Entity.App),
          to: Private_Routes.Apps.home.getUrl(),
          'data-test-id': 'app-list-analytical-apps-crumb'
        }
      ]}
    />
  );

  const mainContent = (
    <Stack alignY='center' grow='1'>
      <Container noPadding>
        <Stack gap={theme.spaces.x32} grow='1' height='100%'>
          <Stack align='space-between' direction='horizontal' alignY='center' width='100%'>
            <Stack gap={theme.spaces.x12} direction='horizontal' alignY='center'>
              <Text id='my-apps-text' level='md' weight={theme.fontWeight.medium}>
                My {getAppEntityName(Entity.App)}
              </Text>
              <StyledBadges tone='gray' aria-label={`${filteredData.length} apps`}>
                <Text level='xs' weight='450'>
                  {filteredData.length}
                </Text>
              </StyledBadges>
            </Stack>
            <SearchInput
              value={searchStr}
              onChange={setSearchStr}
              data-test-id='app-list-search-input'
              aria-label={`Search in My ${getAppEntityName(Entity.App)} table`}
            />
          </Stack>
          <Stack grow='1'>
            <StyledTable
              tableLayout='fixed'
              loading={isQueryLoading}
              rowKey='_id'
              height='100%'
              width='100%'
              showEmptyMessage={{ description: 'No apps found' }}
              columns={getColumns((projectId, searchParams) => {
                navigate(getSqlIDEUrl({ uid: projectId, ...searchParams }));
              })}
              dataSource={filteredData}
              sortConfig={{ sortOrder: SortOrder.desc, sortColumn: 'created' }}
              data-test-id='business-apps-app-list-table'
            />
          </Stack>
        </Stack>
      </Container>
    </Stack>
  );

  return <MetadataPageContainer banner={<SnowflakePwdAuthBanner />} header={header} main={mainContent} />;
}
